import { useMutation } from '@apollo/client'

import {
   NETWORK_ONLY_FETCH_POLICY,
   SendOtpForUpdatingUserPinParams
} from '@flowwai-frontend/bps-officer-web/graphql'
import {
   convertToErrorType,
   CustomError,
   getAPIStatus
} from '@flowwai-frontend/shared-web/common/data-access'
import { APIStatus } from '@flowwai-frontend/shared/api-common'

import {
   SendOtpForUpdatingUserPinMutation as Mutation,
   SendOtpForUpdatingUserPinDocument as MutationDocument,
   SendOtpForUpdatingUserPinMutationVariables as MutationVariables
} from './mutation.generatedTypes'
import useAPIResponseHandler from './responseHandler'

export interface FailureArgsType {
   data?: CustomError<Mutation['sendOtpForUpdatingUserPin']>
   error?: Error | null
}

export interface TriggerAPIArgsType {
   reqObj: SendOtpForUpdatingUserPinParams
   onFailure?: (args: FailureArgsType) => void
   onSuccess?: () => void
}

interface ReturnType {
   apiError: Error | null
   apiStatus: APIStatus
   triggerAPI: (args: TriggerAPIArgsType) => void
}

export const useSendOtpForUserPin = (): ReturnType => {
   const [sendOtpForUpdatingUserPin, { error, loading }] = useMutation<
      Mutation,
      MutationVariables
   >(MutationDocument)

   const { handleAPIResponse } = useAPIResponseHandler()

   const triggerAPI = (args: TriggerAPIArgsType): void => {
      const requestObject: MutationVariables = {
         params: args.reqObj
      }

      sendOtpForUpdatingUserPin({
         fetchPolicy: NETWORK_ONLY_FETCH_POLICY,
         variables: requestObject,
         onError: error => {
            args.onFailure?.({ error })
         },
         onCompleted: data =>
            handleAPIResponse({
               data,
               onHandleError: (
                  errorMessage: string,
                  data: Mutation['sendOtpForUpdatingUserPin']
               ) =>
                  args.onFailure?.({
                     data: new CustomError(errorMessage, data)
                  }),
               onSuccess: args.onSuccess
            })
      })
   }

   const apiError = convertToErrorType(error)

   const apiStatus = getAPIStatus({
      isError: Boolean(error),
      isLoading: loading
   })

   return {
      apiError,
      apiStatus,
      triggerAPI
   }
}
